import { useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart/chart";
import { Report1DateRangePicker } from "./date-range-picker";
import { useDateRangeStore } from "./use-date-range-store";
import { fetchLeadsDataOfAllEmployeeForAdmin } from "@/config/queries/admin/admin-queries";
import { CartesianGrid, LabelList, Line, LineChart, XAxis } from "recharts";

function adjustDates(
  d1: Date,
  d2: Date
): { adjustedD1: Date | string; adjustedD2: Date | string } {
  // Create new Date objects to avoid mutating the original inputs
  const adjustedD1 = new Date(d1);
  const adjustedD2 = new Date(d2);

  // Set d1 time to 00:00:00
  adjustedD1.setUTCHours(0, 0, 0, 0);
  adjustedD1.setUTCDate(adjustedD1.getUTCDate() + 1);

  // Set d2 time to 00:00:00 and add 1 day
  adjustedD2.setUTCHours(0, 0, 0, 0);
  adjustedD2.setUTCDate(adjustedD2.getUTCDate() + 1);

  // Return the adjusted dates
  return { adjustedD1, adjustedD2 };
}

function adjustDatesFirstTime(
  d1: Date,
  d2: Date
): { adjustedD1: Date | string; adjustedD2: Date | string } {
  // Create new Date objects to avoid mutating the original inputs
  const adjustedD1 = new Date(d1);
  const adjustedD2 = new Date(d2);

  // Set d1 time to 00:00:00
  adjustedD1.setUTCHours(0, 0, 0, 0);

  // Set d2 time to 00:00:00 and add 1 day
  adjustedD2.setUTCHours(0, 0, 0, 0);
  adjustedD2.setUTCDate(adjustedD2.getUTCDate() + 1);

  // Return the adjusted dates
  return { adjustedD1, adjustedD2 };
}

function getDatesBetween(d1: Date | string, d2: Date | string): string[] {
  const dateArray: string[] = [];
  let currentDate = moment(d1);

  while (currentDate.isSameOrBefore(d2)) {
    dateArray.push(currentDate.format("YYYY-MM-DD")); // Format date to "YYYY-MM-DD"
    currentDate = currentDate.add(1, "days"); // Increment by one day
  }

  return dateArray;
}

function getRandomColor(): string {
  // Generate a random integer between 0 and 16777215 (0xFFFFFF)
  const randomInt = Math.floor(Math.random() * 16777215);
  // Convert the integer to a hexadecimal string
  const hexString = randomInt.toString(16);
  // Pad with leading zeros if necessary to ensure 6 characters
  return `#${hexString.padStart(6, "0")}`;
}

function filterObjects(
  array: {
    [key: string]: string | number;
  }[]
) {
  if (array.length === 0) return array; // Handle empty array case

  // Get the keys of the first object excluding the "date" key
  const keys = Object.keys(array[0]).filter((key) => key !== "date");

  // Determine which keys to keep
  const keysToKeep = keys.filter((key) => {
    return array.some((obj) => obj[key] !== 0);
  });

  // Return a new array with filtered objects containing only the keys to keep
  return array.map((obj) => {
    const newObj: {
      [key: string]: string | number;
    } = { date: obj.date };
    keysToKeep.forEach((key) => {
      newObj[key] = obj[key];
    });
    return newObj;
  });
}

export const LeadReport3 = () => {
  const key = Cookies.get("key") as string;

  const [firstTime, setFirstTime] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("alloted");

  const { dateRange } = useDateRangeStore();

  const startdate = dateRange?.from as Date;
  const enddate = dateRange?.to as Date;

  const adjusedDates = useMemo(() => {
    const { adjustedD1, adjustedD2 } = adjustDates(startdate, enddate);

    return {
      startdate: adjustedD1,
      enddate: adjustedD2,
    };
  }, [startdate, enddate]);

  const adjusedDatesFirstTime = useMemo(() => {
    const { adjustedD1, adjustedD2 } = adjustDatesFirstTime(startdate, enddate);

    return {
      startdate: adjustedD1,
      enddate: adjustedD2,
    };
  }, [startdate, enddate]);

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["emp-leads-data", startdate, enddate],
    queryFn: () =>
      fetchLeadsDataOfAllEmployeeForAdmin(key)({
        startdate: firstTime
          ? (adjusedDatesFirstTime.startdate as Date)
          : (adjusedDates.startdate as Date),
        enddate: firstTime
          ? (adjusedDatesFirstTime.enddate as Date)
          : (adjusedDates.enddate as Date),
      }),
    refetchOnWindowFocus: false,
  });

  // console.log("Something crazy", data);

  const chartData = useMemo(() => {
    let defaultData: {
      [key: string]: string | number;
    }[] = getDatesBetween(startdate, enddate).map((item) => ({
      date: item,
    }));

    if (isPending) {
      return defaultData;
    }

    if (data === undefined || data?.reports === undefined) {
      return defaultData;
    }

    const reports = data?.reports;

    reports?.forEach((item) => {
      for (const key in item) {
        const leads = item[key];
        if (selectedStatus === "alloted") {
          defaultData = defaultData.map((dd) => {
            const dDate = dd.date as string;
            let foundCount = 0;
            leads.forEach((lead) => {
              const leadDate = moment(lead.allotedAt).format("YYYY-MM-DD");
              if (leadDate === dDate) {
                foundCount += 1;
              }
            });

            return {
              ...dd,
              [key]: foundCount,
            };
          });
        } else if (selectedStatus === "attempted") {
          defaultData = defaultData.map((dd) => {
            const dDate = dd.date as string;
            let foundCount = 0;
            leads.forEach((lead) => {
              const leadDate = moment(lead.allotedAt).format("YYYY-MM-DD");
              if (leadDate === dDate) {
                if (lead.statusChanged) {
                  foundCount++;
                }
              }
            });

            return {
              ...dd,
              [key]: foundCount,
            };
          });
        } else if (selectedStatus === "success") {
          defaultData = defaultData.map((dd) => {
            const dDate = dd.date as string;
            let foundCount = 0;
            leads.forEach((lead) => {
              const leadDate = moment(lead.allotedAt).format("YYYY-MM-DD");
              if (leadDate === dDate) {
                if (lead.statusChanged && lead.status === "Closed") {
                  foundCount++;
                }
              }
            });

            return {
              ...dd,
              [key]: foundCount,
            };
          });
        } else if (selectedStatus === "failed") {
          defaultData = defaultData.map((dd) => {
            const dDate = dd.date as string;
            let foundCount = 0;
            leads.forEach((lead) => {
              const leadDate = moment(lead.allotedAt).format("YYYY-MM-DD");
              if (leadDate === dDate) {
                if (lead.statusChanged && lead.status === "Not Interested") {
                  foundCount++;
                }
              }
            });

            return {
              ...dd,
              [key]: foundCount,
            };
          });
        }
      }
    });

    const filtered = filterObjects(defaultData);

    return filtered;
  }, [data, isPending, isFetching, selectedStatus]);

  // const chartConfig = {
  //   "Shyam Singh": {
  //     label: "Shyam Singh",
  //     color: "hsl(var(--chart-1))",
  //   },
  // } satisfies ChartConfig;

  const memoChartConfig = useMemo(() => {
    if (isPending) {
      return {} as ChartConfig;
    }

    if (data === undefined || data?.reports === undefined) {
      return {} as ChartConfig;
    }

    const reports = data?.reports;

    let config = {} as ChartConfig;

    reports.forEach((report) => {
      for (const key in report) {
        config[key] = {
          label: key,
          color: getRandomColor(),
        };
      }
    });

    return config;
  }, [data, isPending, isFetching]);

  return (
    <Card className="mt-10 mb-10">
      <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
        <div className="grid flex-1 gap-1 text-center sm:text-left">
          <CardTitle>Report 3</CardTitle>
          <CardDescription>
            The report displays the number of leads to each employee over a
            given range of dates. Each line represents a different employee, and
            the y-axis shows the number of leads. For a selected status, such as
            "Alloted," the chart illustrates how many leads were assigned to
            each employee on each day within the specified date range, providing
            a clear comparison of lead assignment trends across employees over
            time.
          </CardDescription>
        </div>
      </CardHeader>
      <div className="border-y p-6 flex items-center justify-between">
        <div>
          <Report1DateRangePicker
            firstTime={firstTime}
            firstTimeChange={() => {
              setFirstTime(false);
            }}
          />
        </div>

        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger
            className="w-[160px] rounded-lg sm:ml-auto"
            aria-label="Select status"
          >
            <SelectValue placeholder="your status" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="alloted" className="rounded-lg">
              Alloted
            </SelectItem>
            <SelectItem value="attempted" className="rounded-lg">
              Attempted
            </SelectItem>
            <SelectItem value="success" className="rounded-lg">
              Success
            </SelectItem>
            <SelectItem value="failed" className="rounded-lg">
              Failed
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <CardContent>
        <ChartContainer config={memoChartConfig} className="h-[450px] w-full">
          <LineChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: 12,
              right: 12,
              top: 30,
              bottom: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  indicator="dot"
                  labelFormatter={(value) => {
                    return new Date(value).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    });
                  }}
                />
              }
            />
            {Object.keys(memoChartConfig).map((mc) => {
              const foundColor = memoChartConfig[mc]?.color;

              return (
                <Line
                  dataKey={mc}
                  type="monotone"
                  stroke={foundColor}
                  strokeWidth={2}
                  dot={{
                    fill: foundColor,
                  }}
                  activeDot={{
                    r: 6,
                  }}
                >
                  <LabelList
                    position="top"
                    offset={12}
                    className="fill-foreground"
                    fontSize={12}
                  />
                </Line>
              );
            })}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
